/**
 * CONTRACT FILE. Destination: src/formats/json/types.ts
 * Implemented by package W1-07a (formats-json-rpe):
 *   - src/formats/json/lexer.ts  exports `parseJsonLossless: ParseJsonLosslessFn`, `nodeToJson: NodeToJsonFn`
 *   - src/formats/json/writer.ts exports `createJsonWriter: CreateJsonWriterFn`
 *
 * Number-preserving JSON (judge-tech §4.5): JSON.parse loses the difference between `1` and `1.0`, which
 * Phira's integer fields reject, and RPEVersion `170.0` (-> 160). Our own small lexer keeps each number's
 * lexeme, works in every browser and worker, and reports precise error positions.
 */

import type { Json } from '../../core/util/types';

export type JsonNode =
  | { readonly k: 'null'; readonly at: number }
  | { readonly k: 'bool'; readonly v: boolean; readonly at: number }
  /** `lex` is the exact source text of the number; `int` is true when it has no '.', 'e' or 'E'. */
  | { readonly k: 'num'; readonly v: number; readonly lex: string; readonly int: boolean; readonly at: number }
  | { readonly k: 'str'; readonly v: string; readonly at: number }
  | { readonly k: 'arr'; readonly items: readonly JsonNode[]; readonly at: number }
  /** Entries in source order; duplicate keys kept (last wins in nodeToJson, as serde_json). */
  | { readonly k: 'obj'; readonly entries: ReadonlyArray<readonly [string, JsonNode]>; readonly at: number };

export interface JsonSyntaxError {
  readonly message: string;
  readonly offset: number;
  readonly line: number;
  readonly col: number;
}

/** Strict JSON (RFC 8259). Leading BOM / whitespace are accepted but reported via `leading`. */
export type ParseJsonLosslessFn = (
  text: string,
) => { readonly ok: true; readonly node: JsonNode; readonly leading: { readonly bom: boolean; readonly whitespace: boolean } } | { readonly ok: false; readonly error: JsonSyntaxError };

export type NodeToJsonFn = (node: JsonNode) => Json;

/** Streaming typed writer used by all encoders (RPE chart, extra.json, sidecar). */
export interface JsonWriter {
  beginObject(): void;
  endObject(): void;
  beginArray(): void;
  endArray(): void;
  key(k: string): void;
  /** Integer kinds assert Number.isInteger and range; throw EncodeError on violation. */
  int(v: number, kind: 'i32' | 'u8' | 'u16' | 'u32'): void;
  /** f32: shortest decimal round-tripping to Math.fround(v). '.0' suffix for integral values when floatStyle 'rpe'. */
  f32(v: number): void;
  /** f64: shortest round-trip decimal. */
  f64(v: number): void;
  bool(v: boolean): void;
  string(v: string): void;
  null(): void;
  /** Passthrough value written verbatim (numbers via shortest form). */
  json(v: Json): void;
  /**
   * A number lexeme written exactly as given (RawNumberLexeme 'float', e.g. `170.0`, so Phira's effective
   * RPEVersion stays 160). Validated against the JSON number grammar (EncodeError otherwise).
   */
  rawNumber(lexeme: string): void;
  /**
   * A JSON string token written exactly as given, escapes included (RawNumberLexeme 'string', e.g. RPEVersion
   * `"\u0031\u0037\u0030"`), so a lexeme compared as text stays equal. EncodeError if it is not one.
   */
  rawString(lexeme: string): void;
  /** UTF-8 bytes (TextEncoder; lone surrogates become U+FFFD so output is always valid UTF-8). */
  finish(): Uint8Array;
}

/** Thrown by writers on invariant violations (a bug: surfaces as a failed export self-check, never a broken file). */
export class EncodeError extends Error {
  readonly path: string;
  constructor(path: string, message: string) {
    super(`${path}: ${message}`);
    this.path = path;
    this.name = 'EncodeError';
  }
}

export type CreateJsonWriterFn = (opts: { readonly pretty: boolean; readonly floatStyle: 'rpe' | 'shortest' }) => JsonWriter;
